import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, secH, listRow } from "../../constants/styles";
import Ic from "../common/Ic";

export default function AdminDash() {
  const { t, h, go, setSelOrgId, flash } = useContext(Ctx);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orgSearch, setOrgSearch] = useState("");
  const [showAllOrgs, setShowAllOrgs] = useState(false);
  const [expandedBill, setExpandedBill] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.adminGetStats();
        if (res.success) setStats(res.stats);
      } catch (e) {
        console.error("Admin load error:", e);
        flash(t.loginFail || "Failed to load", "error");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
      <p style={{ color: h.ts, ...type.sm, marginTop: sp.md }}>{t.connecting || "Loading..."}</p>
    </div>;
  }

  const primaryStats = stats ? [
    { label: "Organizations", value: stats.totalOrgs, icon: "building", color: h.ac },
    { label: "Properties", value: stats.totalProps, icon: "home", color: h.gn },
    { label: "Tenants", value: stats.totalTenants, icon: "users", color: h.pp || h.ac },
    { label: "Payments", value: stats.totalPayments, icon: "credit-card", color: h.wn },
  ] : [];

  const secondaryStats = stats ? [
    { label: "Service Persons", value: stats.totalServicePersons, icon: "wrench" },
    { label: "Leads", value: stats.totalLeads, icon: "target" },
    { label: "Leases", value: stats.totalLeases, icon: "file-text" },
    { label: "Active Orgs", value: stats.activeOrgs, icon: "check-circle" },
  ] : [];

  const navItems = [
    { label: "Organizations", desc: `${stats?.totalOrgs || 0} total`, icon: "building", page: "adminOrgs", color: h.ac },
    { label: "Properties", desc: `${stats?.totalProps || 0} total`, icon: "home", page: "adminProperties", color: h.gn },
    { label: "Tenants", desc: `${stats?.totalTenants || 0} total`, icon: "users", page: "adminTenants", color: h.pp || h.ac },
    { label: "Service Persons", desc: `${stats?.totalServicePersons || 0} total`, icon: "wrench", page: "adminServicePersons", color: h.wn },
    { label: "Pricing & Plans", desc: "Manage plan config", icon: "settings", page: "adminPricing", color: h.ac },
    { label: "Platform Reports", desc: "Export CSV reports", icon: "grid", page: "adminReports", color: h.gn },
    { label: "Feedback Inbox", desc: stats?.newFeedback > 0 ? `${stats.newFeedback} new reports` : "Bug reports & feedback", icon: "msg", page: "adminFeedback", color: "#ef4444", badge: stats?.newFeedback },
  ];

  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    {/* Header */}
    <div style={{ marginBottom: sp.xl }}>
      <h2 style={{ ...type.h1, color: h.tx, margin: 0 }}>Platform Overview</h2>
      <p style={{ ...type.sm, color: h.ts, marginTop: sp.xs }}>Super Admin Dashboard</p>
    </div>

    {/* Primary Stats Grid */}
    {stats && <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
        {primaryStats.map((s, i) => <div key={i} style={{ ...crd(h), padding: sp.lg, display: "flex", alignItems: "center", gap: sp.md }}>
          <div style={icBox(s.color, 40)}>
            <Ic n={s.icon} s={18} c={s.color} />
          </div>
          <div>
            <div style={{ ...type.h2, color: h.tx }}>{s.value}</div>
            <div style={{ ...type.xs, color: h.ts, marginTop: 2 }}>{s.label}</div>
          </div>
        </div>)}
      </div>

      {/* Secondary Stats Row */}
      <div style={{ ...crd(h), display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: sp.xs, padding: sp.md }}>
        {secondaryStats.map((s, i) => <div key={i} style={{ textAlign: "center", padding: sp.sm }}>
          <div style={{ ...type.h3, color: h.tx }}>{s.value}</div>
          <div style={{ fontSize: 9, fontWeight: 500, color: h.tt, marginTop: 2 }}>{s.label}</div>
        </div>)}
      </div>

      {/* Plan Distribution + Revenue */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginTop: sp.lg }}>
        <div style={{ ...crd(h), padding: sp.lg }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Plan Split</div>
          {stats.planSplit ? <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-around", alignItems: "center", gap: sp.sm }}>
            {Object.entries(stats.planSplit).map(([planId, info], i) => {
              const colors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
              const col = colors[planId] || h.ac;
              return <div key={planId} style={{ textAlign: "center", minWidth: 50 }}>
                <div style={{ ...type.h3, color: col }}>{info.count}</div>
                <span style={{ ...bdg(`${col}20`, col), fontSize: 9 }}>{info.name}</span>
              </div>;
            })}
          </div> : <div style={{ display: "flex", justifyContent: "space-around", alignItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ ...type.h2, color: h.wn }}>{stats.freeOrgs}</div>
              <span style={bdg(`${h.wn}20`, h.wn)}>FREE</span>
            </div>
            <div style={{ width: 1, height: 30, background: h.bd }} />
            <div style={{ textAlign: "center" }}>
              <div style={{ ...type.h2, color: h.gn }}>{stats.proOrgs}</div>
              <span style={bdg(`${h.gn}20`, h.gn)}>PAID</span>
            </div>
          </div>}
        </div>
        <div style={{ ...crd(h), padding: sp.lg }}>
          <div style={{ ...type.cap, color: h.ts, marginBottom: sp.md }}>Revenue</div>
          <div style={{ ...type.h2, color: h.gn, marginBottom: sp.xs }}>{"\u20B9"}{stats.totalRevenue || 0}</div>
          <div style={{ ...type.xs, color: h.ts }}>This month: {"\u20B9"}{stats.monthRevenue || 0}</div>
        </div>
      </div>
    </>}

    {/* Quick Navigation */}
    <div style={{ marginTop: sp.xxl }}>
      <div style={secH(h)}>
        <Ic n="grid" s={16} c={h.ac} />
        Manage
      </div>
      <div style={{ ...crd(h), padding: 0, overflow: "hidden" }}>
        {navItems.map((item, i) => (
          <div key={i}
            onClick={() => go(item.page)}
            style={{
              ...listRow(h, i === navItems.length - 1),
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
              <div style={icBox(item.color, 36)}>
                <Ic n={item.icon} s={16} c={item.color} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: sp.xs }}>
                  <div style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{item.label}</div>
                  {item.badge > 0 && <span style={{ background: "#ef4444", color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{item.badge}</span>}
                </div>
                <div style={{ ...type.xs, color: h.tt }}>{item.desc}</div>
              </div>
            </div>
            <Ic n="chevron-right" s={16} c={h.tt} />
          </div>
        ))}
      </div>
    </div>

    {/* Organizations */}
    {stats?.recentOrgs?.length > 0 && <div style={{ marginTop: sp.xxl }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.md }}>
        <div style={secH(h)}>
          <Ic n="building" s={16} c={h.ac} />
          Organizations
        </div>
        <span onClick={() => go("adminOrgs")} style={{ ...type.xs, color: h.ac, cursor: "pointer", fontWeight: 500 }}>View All ({stats.totalOrgs})</span>
      </div>

      {/* Search */}
      <div style={{ position: "relative", marginBottom: sp.md }}>
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }}>
          <Ic n="search" s={14} c={h.tt} />
        </div>
        <input value={orgSearch} onChange={e => { setOrgSearch(e.target.value); setShowAllOrgs(false); }}
          placeholder="Search organization..."
          style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, paddingLeft: 34, borderRadius: rad.md, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, ...type.xs, outline: "none", boxSizing: "border-box" }}
        />
      </div>

      {(() => {
        const filtered = (stats.recentOrgs || []).filter(o => {
          if (!orgSearch) return true;
          const q = orgSearch.toLowerCase();
          return (o.name || "").toLowerCase().includes(q) || (o.owner_phone || "").includes(q) || (o.slug || "").toLowerCase().includes(q);
        });
        const visible = showAllOrgs ? filtered : filtered.slice(0, 5);
        const hasMore = filtered.length > 5 && !showAllOrgs;

        return <>
          {visible.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: sp.lg }}>
            <div style={{ ...type.xs, color: h.tt }}>No organizations match "{orgSearch}"</div>
          </div>}

          {visible.map(org => {
            const planColors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
            const pCol = planColors[org.plan] || h.ac;
            const pLabel = org.plan_name || (org.plan || "free").toUpperCase();
            return <div key={org.id}
              onClick={() => { setSelOrgId(org.id); go("adminOrgDetail"); }}
              style={{ ...crd(h), cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.xs }}>
                  <span style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{org.name}</span>
                  <span style={bdg(`${pCol}20`, pCol)}>{pLabel}</span>
                  {!org.active && <span style={bdg(`${h.rd}20`, h.rd)}>Inactive</span>}
                </div>
                <div style={{ ...type.xs, color: h.ts }}>
                  {org.owner_phone || "No phone"} {"\u00B7"} Created {org.created_at ? new Date(org.created_at).toLocaleDateString() : "—"}
                  {org.plan_end && <span> {"\u00B7"} Expires {new Date(org.plan_end).toLocaleDateString()}</span>}
                </div>
              </div>
              <Ic n="chevron-right" s={16} c={h.ac} />
            </div>;
          })}

          {hasMore && <div
            onClick={() => setShowAllOrgs(true)}
            style={{ textAlign: "center", padding: sp.md, cursor: "pointer" }}
          >
            <span style={{ ...type.xs, color: h.ac, fontWeight: 600 }}>Show More ({filtered.length - 5} more)</span>
          </div>}
        </>;
      })()}
    </div>}

    {/* Recent Billing */}
    {stats?.recentBilling?.length > 0 && <div style={{ marginTop: sp.xxl, marginBottom: sp.xxxl }}>
      <div style={secH(h)}>
        <Ic n="credit-card" s={16} c={h.gn} />
        Recent Billing
      </div>
      {stats.recentBilling.map((b, i) => {
        const ok = b.status === "captured" || b.status === "verified";
        const fail = b.status === "failed";
        const planLabel = b.plan_name || (b.plan_type || "").charAt(0).toUpperCase() + (b.plan_type || "").slice(1);
        const planColors = { free: h.wn, starter: h.ac, growth: h.pp || "#8b5cf6", business: h.gn, pro: h.gn };
        const pCol = planColors[b.plan_type] || h.ac;
        const isExpanded = expandedBill === (b.id || i);
        const detailRow = (label, value) => value ? <div style={{ display: "flex", justifyContent: "space-between", padding: `${sp.xs}px 0`, borderBottom: `1px solid ${h.bd}` }}>
          <span style={{ ...type.xs, color: h.ts }}>{label}</span>
          <span style={{ ...type.xs, color: h.tx, fontWeight: 500, textAlign: "right", maxWidth: "60%", wordBreak: "break-all" }}>{value}</span>
        </div> : null;

        return <div key={b.id || i}
          onClick={() => setExpandedBill(isExpanded ? null : (b.id || i))}
          style={{ ...crd(h), cursor: "pointer", border: isExpanded ? `1.5px solid ${h.ac}30` : undefined }}
        >
          {/* Summary row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.xs }}>
                <span style={{ ...type.sm, color: h.tx, fontWeight: 600 }}>{"\u20B9"}{b.amount}</span>
                <span style={{ ...bdg(`${pCol}20`, pCol), fontSize: 9 }}>{planLabel}</span>
                <span style={bdg(
                  ok ? `${h.gn}20` : fail ? `${h.rd}20` : `${h.wn}20`,
                  ok ? h.gn : fail ? h.rd : h.wn
                )}>{ok ? "Paid" : fail ? "Failed" : b.status || "pending"}</span>
              </div>
              <div style={{ ...type.xs, color: h.ts }}>
                {b.org_name || b.org_id || "—"} {b.org_phone ? `(${b.org_phone})` : ""}
              </div>
            </div>
            <Ic n={isExpanded ? "chevron-up" : "chevron-down"} s={16} c={h.tt} />
          </div>

          {/* Expanded details */}
          {isExpanded && <div style={{ marginTop: sp.md, paddingTop: sp.md, borderTop: `1px solid ${h.bd}` }}>
            {detailRow("Organization", b.org_name && b.org_name !== b.org_id ? b.org_name : "—")}
            {detailRow("Org ID", b.org_id || "—")}
            {detailRow("Phone", b.org_phone)}
            {detailRow("Plan", planLabel)}
            {detailRow("Amount (Paise)", b.amount_paise?.toLocaleString())}
            {detailRow("Amount", `\u20B9${b.amount}`)}
            {detailRow("Currency", b.currency || "INR")}
            {detailRow("Status", ok ? "Verified / Paid" : fail ? "Failed" : b.status)}
            {detailRow("Payment Source", b.source || "—")}
            {detailRow("Order Date", b.created_at ? new Date(b.created_at).toLocaleString() : "—")}
            {detailRow("Verified At", b.verified_at ? new Date(b.verified_at).toLocaleString() : "Not verified")}
            {detailRow("Razorpay Order ID", b.razorpay_order_id)}
            {detailRow("Razorpay Payment ID", b.razorpay_payment_id)}
            {detailRow("Transaction ID", b.id)}
            {b.notes && detailRow("Notes", b.notes)}

            {/* Quick action — go to org */}
            {b.org_id && <div
              onClick={(e) => { e.stopPropagation(); setSelOrgId(b.org_id); go("adminOrgDetail"); }}
              style={{ marginTop: sp.md, textAlign: "center", padding: sp.sm, background: `${h.ac}10`, borderRadius: rad.md, cursor: "pointer" }}
            >
              <span style={{ ...type.xs, color: h.ac, fontWeight: 600 }}>View Organization →</span>
            </div>}
          </div>}
        </div>;
      })}
    </div>}
  </div>;
}
